import { useState, useEffect, useMemo, useRef, useId, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Bell,
  Briefcase,
  Globe,
  Code2,
  LayoutDashboard,
  Lightbulb,
  LogOut,
  PanelLeftClose,
  PanelRightClose,
  PenTool,
  Plus,
  Settings,
  Stamp,
  Menu,
  Search,
  FolderOpen,
  Mail,
  Radar,
  ChevronDown,
  ShieldAlert,
  Users,
  Building2,
  KeyRound,
  type LucideIcon
} from "lucide-react";
import { authService } from "@/services/auth";
import { cn } from "@/lib/utils";
import { NovoProcessoModal } from "@/components/dashboard/NovoProcessoModal";
import { GlobalSearch } from "@/components/GlobalSearch";
import { AIPanelLive } from "@/components/AIPanelLive";
import { WorkspaceTabsBar } from "@/components/workspace/WorkspaceTabsBar";
import { ExtensionStatusIndicator } from "@/components/shared/ExtensionStatusIndicator";
import { useAIPanel } from "@/contexts/AIPanelContext";
import { useWorkspace, WorkspaceTabRenderer } from "@/contexts/WorkspaceContext";
import { ModuleShellErrorBoundary } from "@/components/shared/ModuleShellErrorBoundary";
import { getWorkspaceRouteLabel } from "@/contexts/workspaceState";
import "./AppLayout.css";

type SubnavItem = {
  label: string;
  path: string;
  icon?: React.ElementType;
  group?: string;
};

type Module = {
  id: string;
  label: string;
  shortLabel: string;
  icon: React.ElementType;
  colorClass: string;
  cssClass: string;
  basePath: string;
  matchers: string[];
  subnav: SubnavItem[];
  hidden?: boolean;
};

const getIconForPath = (path: string) => {
  if (path.includes('dashboard') || path === '/geral' || path === '/patentes' || path === '/marcas') return LayoutDashboard;
  if (path.includes('pesquisa') || path.includes('search')) return Search;
  if (path.includes('meus') || path.includes('processos') || path.includes('oportunidades') || path.includes('clientes')) return FolderOpen;
  if (path.includes('collision')) return ShieldAlert;
  if (path.includes('alertas') || path.includes('monitor')) return Bell;
  if (path.includes('redacao') || path.includes('writer')) return PenTool;
  if (path.includes('operacional') || path.includes('financeiro') || path.includes('atendimentos') || path.includes('kanban')) return Settings;
  if (path.includes('email') || path.includes('correio')) return Mail;
  return LayoutDashboard;
};

const modules: Module[] = [
  {
    id: "geral",
    label: "Página Inicial",
    shortLabel: "PI",
    icon: LayoutDashboard,
    colorClass: "text-sky-400",
    cssClass: "",
    basePath: "/geral",
    matchers: ["/geral", "/dashboard", "/alerts", "/processos-unificados"],
    subnav: [
      { label: "Página Inicial", path: "/geral", group: "PRINCIPAL", icon: LayoutDashboard },
      { label: "BI Estratégico", path: "/geral/bi-estrategico", group: "INTELIGÊNCIA", icon: LayoutDashboard },
      { label: "BI Operacional", path: "/geral/bi-operacional", group: "INTELIGÊNCIA", icon: LayoutDashboard },
      { label: "Processos", path: "/processos-unificados", group: "OUTROS", icon: FolderOpen },
      { label: "Alertas", path: "/alerts", group: "OUTROS", icon: Bell },
    ],
  },
  {
    id: "comercial",
    label: "NIP CRM",
    shortLabel: "CRM",
    icon: Briefcase,
    colorClass: "text-emerald-400",
    cssClass: "",
    basePath: "/comercial",
    matchers: ["/comercial", "/clients", "/demands", "/financeiro", "/crm"],
    subnav: [
      { label: "Página Inicial CRM", path: "/comercial", group: "PRINCIPAL", icon: LayoutDashboard },
      { label: "Pipeline (Kanban)", path: "/comercial/kanban", group: "CRM", icon: LayoutDashboard },
      { label: "Atendimentos", path: "/comercial/atendimentos", group: "CRM", icon: FolderOpen },
      { label: "Clientes", path: "/comercial/clientes", group: "CRM", icon: Users },
      { label: "Financeiro", path: "/financeiro", group: "OUTROS", icon: Briefcase },
    ],
  },
  {
    id: "marcas",
    label: "NIP TM",
    shortLabel: "TM",
    icon: Stamp,
    colorClass: "text-blue-400",
    cssClass: "",
    basePath: "/marcas",
    matchers: ["/marcas"],
    subnav: [
      { label: "Dashboard", path: "/marcas", group: "PRINCIPAL", icon: LayoutDashboard },
      { label: "Meus Processos TM", path: "/marcas/meus-processos", group: "PRINCIPAL", icon: FolderOpen },
      { label: "Buscas TM", path: "/marcas/busca-global", group: "PRINCIPAL", icon: Search },
      { label: "Monitoramento TM", path: "/marcas/monitor", group: "PRINCIPAL", icon: Bell },
      { label: "E-mail ao Cliente", path: "/marcas/email", group: "OUTROS", icon: Mail },
      { label: "Extras", path: "/marcas/extras", group: "OUTROS", icon: FolderOpen },
    ],
  },
  {
    id: "patentes",
    label: "NIP Pat",
    shortLabel: "PAT",
    icon: Lightbulb,
    colorClass: "text-amber-400",
    cssClass: "",
    basePath: "/patentes",
    matchers: ["/patentes", "/research", "/monitoring", "/base", "/search", "/knowledge-base"],
    subnav: [
      { label: "Dashboard", path: "/patentes", group: "PRINCIPAL", icon: LayoutDashboard },
      { label: "Buscas Pat", path: "/patentes/busca", group: "PRINCIPAL", icon: Search },
      { label: "Monitor Mercado", path: "/monitoring/mercado", group: "PRINCIPAL", icon: Search },
      { label: "Grupos Monitoramento", path: "/monitoring/grupos", group: "PRINCIPAL", icon: Users },
      { label: "Monitoramento Patentes", path: "/patentes/monitor", group: "IA NIPSYS ★", icon: Bell },
      { label: "NIP Pat Search", path: "/patentes/nip-search", group: "IA NIPSYS", icon: Search },
      { label: "NIP Writer", path: "/patentes/redacao", group: "IA NIPSYS", icon: PenTool },
      { label: "Meus Processos Pat", path: "/patentes/processos", group: "OUTROS", icon: FolderOpen },
      { label: "Internacional", path: "/patentes/internacional", group: "OUTROS", icon: Globe },
      { label: "E-mail ao Cliente", path: "/patentes/email", group: "OUTROS", icon: Mail },
    ],
  },
  {
    id: "di",
    label: "NIP DI",
    shortLabel: "DI",
    icon: PenTool,
    colorClass: "text-rose-400",
    cssClass: "",
    basePath: "/di",
    matchers: ["/di"],
    subnav: [
      { label: "Dashboard", path: "/di", group: "PRINCIPAL", icon: LayoutDashboard },
      { label: "Meus Processos DI", path: "/di/meus-processos", group: "PRINCIPAL", icon: FolderOpen },
      { label: "Buscas DI", path: "/di/busca-inpi", group: "PRINCIPAL", icon: Search },
      { label: "Monitoramento DI", path: "/di/monitor", group: "PRINCIPAL", icon: Radar },
      { label: "E-mail ao Cliente", path: "/di/email", group: "OUTROS", icon: Mail },
    ],
  },
  {
    id: "software",
    label: "Software",
    shortLabel: "SW",
    icon: Code2,
    colorClass: "text-sky-500",
    cssClass: "",
    basePath: "/software",
    matchers: ["/software"],
    subnav: [
      { label: "Dashboard", path: "/software", group: "PRINCIPAL", icon: LayoutDashboard },
    ],
  },
];

const utilityLinks = [
  { id: "cadastros", label: "Cadastros", icon: Users, path: "/cadastros" },
  { id: "configuracoes", label: "Configurações", icon: Settings, path: "/settings" },
  { id: "operacoes", label: "Operações Internas", icon: Building2, path: "/operacional" },
];

const globalUtilityLinks = [
  { id: "platform-home", label: "Super Admin", icon: ShieldAlert, path: "/platform" },
  { id: "platform-tenants", label: "Tenants", icon: Building2, path: "/platform/tenants" },
  { id: "platform-users", label: "Usuários", icon: Users, path: "/platform/users" },
  { id: "platform-api-keys", label: "Chaves de API", icon: KeyRound, path: "/platform/api-keys" },
];

export function WorkspaceShell() {
  const {
    activeTabId,
    activeModuleId,
    visibleTabs,
    openTab,
    closeTab,
    closeAllTabs,
    pinTab,
    unpinTab,
    setActiveTabId,
  } = useWorkspace();
  const [collapsed, setCollapsed] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [pendingModuleMenuFocusIndex, setPendingModuleMenuFocusIndex] = useState(0);
  const [pendingUserMenuFocusIndex, setPendingUserMenuFocusIndex] = useState(0);
  const { collapsed: aiCollapsed, toggleCollapsed: toggleAiPanel } = useAIPanel();
  const [novoOpen, setNovoOpen] = useState(false);
  const moduleDropdownWrapRef = useRef<HTMLDivElement | null>(null);
  const moduleDropdownButtonRef = useRef<HTMLButtonElement | null>(null);
  const moduleMenuRef = useRef<HTMLDivElement | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  const userMenuButtonRef = useRef<HTMLButtonElement | null>(null);
  const userMenuListRef = useRef<HTMLDivElement | null>(null);
  const moduleDropdownId = useId();
  const userMenuId = useId();

  const user = authService.getUser();
  const menuProfile = user?.menuProfile
    || (typeof authService.getMenuProfile === "function"
      ? authService.getMenuProfile()
      : "user");
  const userMenuTriggerLabel = user?.name
    ? `Abrir menu do usuário de ${user.name}`
    : "Abrir menu do usuário";
  const canAccessTenantAdminArea = menuProfile === 'tenant_admin';
  const canAccessGlobalAdminArea = menuProfile === 'platform';
  const sidebarToggleLabel = collapsed ? "Expandir navegacao lateral" : "Recolher navegacao lateral";
  const topbarSidebarToggleLabel = collapsed ? "Mostrar navegacao lateral" : "Ocultar navegacao lateral";

  const activeModule = useMemo(() => {
    const match = modules.find((module) => module.id === activeModuleId);
    return match || modules[0];
  }, [activeModuleId]);

  const toggleSidebar = () => setCollapsed(!collapsed);

  const availableModules = modules.filter(m => !m.hidden);
  const userMenuItemCount = 2;
  
  const canOpenNew = ["comercial", "patentes", "marcas", "di", "autoral"].includes(activeModule.id);
  const newLabel = 
    activeModule.id === "marcas"
      ? "Meus Processos TM"
      : activeModule.id === "di"
        ? "Meus Processos DI"
        : activeModule.id === "patentes"
          ? "Meus Processos Pat"
      : activeModule.id === "comercial"
        ? "Novo Atendimento"
        : activeModule.id === "autoral"
          ? "Nova Obra"
          : "Novo Processo";

  const getMenuItems = useCallback((menu: HTMLDivElement | null) => {
    if (!menu) return [];
    return Array.from(menu.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'));
  }, []);

  const focusMenuItem = useCallback((menu: HTMLDivElement | null, index: number) => {
    const items = getMenuItems(menu);
    if (items.length === 0) return;
    const normalizedIndex = ((index % items.length) + items.length) % items.length;
    items[normalizedIndex]?.focus();
  }, [getMenuItems]);

  const focusLastMenuItem = useCallback((menu: HTMLDivElement | null) => {
    const items = getMenuItems(menu);
    if (items.length === 0) return;
    items[items.length - 1]?.focus();
  }, [getMenuItems]);

  const moveMenuFocus = useCallback((menu: HTMLDivElement | null, direction: 1 | -1) => {
    const items = getMenuItems(menu);
    if (items.length === 0) return;

    const activeIndex = items.findIndex((item) => item === document.activeElement);
    const nextIndex = activeIndex === -1
      ? direction === 1 ? 0 : items.length - 1
      : activeIndex + direction;

    focusMenuItem(menu, nextIndex);
  }, [focusMenuItem, getMenuItems]);

  useEffect(() => {
    function handleOutsideClick(event: MouseEvent) {
      const target = event.target as Node;

      if (dropdownOpen && !moduleDropdownWrapRef.current?.contains(target)) {
        closeModuleDropdown();
      }

      if (userMenuOpen && !userMenuRef.current?.contains(target)) {
        closeUserMenu();
      }
    }

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [dropdownOpen, userMenuOpen]);

  useEffect(() => {
    if (dropdownOpen) {
      focusMenuItem(moduleMenuRef.current, pendingModuleMenuFocusIndex);
    }
  }, [dropdownOpen, pendingModuleMenuFocusIndex, focusMenuItem]);

  useEffect(() => {
    if (userMenuOpen) {
      focusMenuItem(userMenuListRef.current, pendingUserMenuFocusIndex);
    }
  }, [userMenuOpen, pendingUserMenuFocusIndex, focusMenuItem]);

  function closeModuleDropdown({ restoreFocus = false }: { restoreFocus?: boolean } = {}) {
    setDropdownOpen(false);
    if (restoreFocus) {
      moduleDropdownButtonRef.current?.focus();
    }
  }

  function closeUserMenu({ restoreFocus = false }: { restoreFocus?: boolean } = {}) {
    setUserMenuOpen(false);
    if (restoreFocus) {
      userMenuButtonRef.current?.focus();
    }
  }

  function handleModuleDropdownTriggerKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "ArrowDown" || event.key === "Home") {
      event.preventDefault();
      setPendingModuleMenuFocusIndex(0);
      setDropdownOpen(true);
      return;
    }

    if (event.key === "ArrowUp" || event.key === "End") {
      event.preventDefault();
      setPendingModuleMenuFocusIndex(Math.max(availableModules.length - 1, 0));
      setDropdownOpen(true);
      return;
    }

    if (event.key === "Escape" && dropdownOpen) {
      event.preventDefault();
      closeModuleDropdown({ restoreFocus: true });
    }
  }

  function handleModuleDropdownKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveMenuFocus(moduleMenuRef.current, 1);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveMenuFocus(moduleMenuRef.current, -1);
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      focusMenuItem(moduleMenuRef.current, 0);
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      focusLastMenuItem(moduleMenuRef.current);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closeModuleDropdown({ restoreFocus: true });
    }
  }

  function handleUserMenuTriggerKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    if (event.key === "ArrowDown" || event.key === "Home") {
      event.preventDefault();
      setPendingUserMenuFocusIndex(0);
      setUserMenuOpen(true);
      return;
    }

    if (event.key === "ArrowUp" || event.key === "End") {
      event.preventDefault();
      setPendingUserMenuFocusIndex(userMenuItemCount - 1);
      setUserMenuOpen(true);
      return;
    }

    if (event.key === "Escape" && userMenuOpen) {
      event.preventDefault();
      closeUserMenu({ restoreFocus: true });
    }
  }

  function handleUserMenuKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      moveMenuFocus(userMenuListRef.current, 1);
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      moveMenuFocus(userMenuListRef.current, -1);
      return;
    }

    if (event.key === "Home") {
      event.preventDefault();
      focusMenuItem(userMenuListRef.current, 0);
      return;
    }

    if (event.key === "End") {
      event.preventDefault();
      focusLastMenuItem(userMenuListRef.current);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      closeUserMenu({ restoreFocus: true });
    }
  }

  function resolveTabLabel(path: string, fallbackLabel: string) {
    return getWorkspaceRouteLabel(path) ?? fallbackLabel;
  }

  const renderNavItem = ({
    key,
    label,
    path,
    Icon,
    active,
    className,
  }: {
    key: string;
    label: string;
    path: string;
    Icon: React.ElementType;
    active: boolean;
    className?: string;
  }) => (
    <button
      key={key}
      type="button"
      className={cn("nav-item", className, active && "active")}
      onClick={() => openTab(path, resolveTabLabel(path, label), Icon as LucideIcon)}
      title={collapsed ? label : undefined}
      aria-label={collapsed ? label : undefined}
      data-collapsed-tooltip={collapsed ? label : undefined}
      data-collapsed-cue={collapsed ? "true" : undefined}
    >
      <Icon className="w-4 h-4" />
      <span className="nav-collapsed-cue" aria-hidden="true" />
      <span className="nav-label">{label}</span>
    </button>
  );

  return (
    <div className={cn("nipsys-app transition-colors duration-500", activeModule.cssClass)}>
      {/* SIDEBAR */}
      <div className={`sidebar ${collapsed ? 'collapsed' : ''}`} id="sidebar">
        <div className="sidebar-top">
          <Link to="/geral" className="sidebar-logo">
            <img src="/Logo NIPSYS + Icone(1)_clara.png" alt="NIPSYS" className="logo-img" />
          </Link>

          <div className="sidebar-module-wrap" ref={moduleDropdownWrapRef}>
            <button
              ref={moduleDropdownButtonRef}
              type="button"
              className="sidebar-module"
              onClick={() => {
                setPendingModuleMenuFocusIndex(0);
                setDropdownOpen((current) => !current);
              }}
              onKeyDown={handleModuleDropdownTriggerKeyDown}
              aria-expanded={dropdownOpen}
              aria-haspopup="menu"
              aria-controls={moduleDropdownId}
              aria-label={`Selecionar módulo, atual ${activeModule.label}`}
              title={collapsed ? `Módulo atual: ${activeModule.label}` : undefined}
              data-collapsed-tooltip={collapsed ? activeModule.label : undefined}
              data-collapsed-cue={collapsed ? "true" : undefined}
            >
              <div className="sidebar-module-info">
                <div className="sidebar-module-tag">MÓDULO ATIVO</div>
                <div className="sidebar-module-name">{activeModule.label}</div>
              </div>
              <div className="sidebar-module-collapsed" aria-hidden={!collapsed}>
                <activeModule.icon className="w-4 h-4" />
                <span>{activeModule.shortLabel}</span>
              </div>
              <ChevronDown className={cn("arrow-icon w-4 h-4 text-white/40 transition-transform duration-200", dropdownOpen && "rotate-180")} />
            </button>

            <div
              ref={moduleMenuRef}
              id={moduleDropdownId}
              className={cn("module-dropdown", dropdownOpen && "open")}
              role="menu"
              aria-label="Seletor de módulos"
              aria-hidden={!dropdownOpen}
              onKeyDown={handleModuleDropdownKeyDown}
            >
              {availableModules.map((mod, index) => (
                <button
                  key={mod.id}
                  type="button"
                  role="menuitem"
                  className={cn("mod-item", mod.id === activeModule.id && "active")}
                  tabIndex={dropdownOpen ? 0 : -1}
                  onClick={() => {
                    closeModuleDropdown();
                    openTab(mod.basePath, resolveTabLabel(mod.basePath, mod.label), mod.icon as LucideIcon);
                  }}
                >
                  <mod.icon className="w-4 h-4 shrink-0" />
                  <span>{mod.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="sidebar-nav overflow-y-auto" style={{ maxHeight: "calc(100vh - 200px)" }}>
          {Object.entries(
            activeModule.subnav.reduce((acc, item) => {
              const group = item.group || "NAVEGAÇÃO";
              if (!acc[group]) acc[group] = [];
              acc[group].push(item);
              return acc;
            }, {} as Record<string, SubnavItem[]>)
          ).map(([group, items]) => (
            <div key={group} className="sidebar-group">
              <div className="nav-section-label">{group}</div>
              {items.map((item) => {
                const active = activeTabId === item.path;
                const Icon = item.icon || getIconForPath(item.path);
                return renderNavItem({
                  key: item.path,
                  label: item.label,
                  path: item.path,
                  Icon,
                  active,
                });
              })}
            </div>
          ))}

          {/* ADMIN / OPERATIONAL SECTION */}
          {(canAccessTenantAdminArea || canAccessGlobalAdminArea) && (
            <div className="sidebar-group border-t border-white/5 pt-4 mt-4">
              <div className="nav-section-label">
                {canAccessGlobalAdminArea ? "SUPER ADMIN" : "ADMIN / OPERAÇÕES"}
              </div>
              {canAccessTenantAdminArea
                ? utilityLinks.map((link) => {
                    const active = activeTabId === link.path;
                    const Icon = link.icon;
                    return renderNavItem({
                      key: link.id,
                      label: link.label,
                      path: link.path,
                      Icon,
                      active,
                      className: "opacity-80 hover:opacity-100",
                    });
                  })
                : null}
              {canAccessGlobalAdminArea
                ? globalUtilityLinks.map((link) => {
                    const active = activeTabId === link.path;
                    const Icon = link.icon;
                    return renderNavItem({
                      key: link.id,
                      label: link.label,
                      path: link.path,
                      Icon,
                      active,
                      className: "opacity-80 hover:opacity-100",
                    });
                  })
                : null}
            </div>
          )}
        </div>

        <button
          type="button"
          className="sidebar-collapse-btn"
          onClick={toggleSidebar}
          title={sidebarToggleLabel}
          aria-label={sidebarToggleLabel}
          data-collapsed-tooltip={collapsed ? sidebarToggleLabel : undefined}
          data-collapsed-cue={collapsed ? "true" : undefined}
        >
          {collapsed ? <PanelRightClose className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
          <span className="nav-label">{collapsed ? '' : 'Recolher'}</span>
        </button>

        <div className="sidebar-user-wrap" ref={userMenuRef}>
          <button
            ref={userMenuButtonRef}
            type="button"
            className="sidebar-user"
            onClick={() => {
              setPendingUserMenuFocusIndex(0);
              setUserMenuOpen((current) => !current);
            }}
            onKeyDown={handleUserMenuTriggerKeyDown}
            title="Abrir menu do usuário"
            aria-label={userMenuTriggerLabel}
            aria-expanded={userMenuOpen}
            aria-haspopup="menu"
            aria-controls={userMenuId}
            data-collapsed-tooltip={collapsed ? userMenuTriggerLabel : undefined}
            data-collapsed-cue={collapsed ? "true" : undefined}
          >
            <div className="user-av">{user?.name?.substring(0, 2).toUpperCase() || "US"}</div>
            <div className="user-info">
              <div className="user-name">{user?.name || "Usuário"}</div>
              <div className="user-role">{user?.role || "Equipe"}</div>
            </div>
            <ChevronDown className={cn("user-menu-chevron h-4 w-4 transition-transform duration-200", userMenuOpen && "rotate-180")} />
          </button>
          <div
            ref={userMenuListRef}
            id={userMenuId}
            className={cn("sidebar-user-menu", userMenuOpen && "open")}
            role="menu"
            aria-label="Ações da conta"
            aria-hidden={!userMenuOpen}
            onKeyDown={handleUserMenuKeyDown}
          >
            <button
              type="button"
              className="sidebar-user-menu-item"
              role="menuitem"
              onClick={() => {
                closeUserMenu();
                authService.switchUser();
              }}
            >
              <Users className="h-4 w-4" />
              <span>Trocar usuário</span>
            </button>
            <button
              type="button"
              className="sidebar-user-menu-item danger"
              role="menuitem"
              onClick={() => {
                closeUserMenu();
                authService.logout();
              }}
            >
              <LogOut className="h-4 w-4" />
              <span>Sair</span>
            </button>
          </div>
        </div>
      </div>

      {/* MAIN */}
      <div className="main flex flex-col h-screen">
        {/* UNIFIED TOPBAR / TAB BAR */}
        <div className="topbar h-12 flex-shrink-0 flex items-stretch px-0 border-b border-slate-200 bg-slate-50 relative">
          <div className="flex items-center justify-center h-full px-4 border-r border-slate-200 bg-white">
            <button
              type="button"
              className="sidebar-toggle hover:bg-slate-100 p-1.5 rounded-md transition-colors"
              onClick={toggleSidebar}
              style={{ color: 'var(--navy)' }}
              aria-label={topbarSidebarToggleLabel}
              title={topbarSidebarToggleLabel}
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>
          
          <WorkspaceTabsBar
            tabs={visibleTabs}
            activeTabId={activeTabId}
            onActivate={setActiveTabId}
            onClose={closeTab}
            onPin={pinTab}
            onUnpin={unpinTab}
            onCloseAll={closeAllTabs}
          />

          {/* RIGHT ACTIONS */}
          <div className="flex items-center h-full px-4 gap-2 border-l border-slate-200 bg-white shadow-[-4px_0_10px_rgba(0,0,0,0.02)] z-10 shrink-0">
            <ExtensionStatusIndicator />
            <GlobalSearch />
            <button className="tb-btn danger !h-8 !text-[11px]" onClick={() => setNovoOpen(true)}>
              <svg width="10" height="10" viewBox="0 0 16 16" fill="none" style={{ marginRight: '2px' }}><circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" /><path d="M8 5v3l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
              Demanda
            </button>
            {canOpenNew && (
              <button className="tb-btn primary !h-8 !text-[11px]" onClick={() => setNovoOpen(true)}>
                <Plus className="w-3.5 h-3.5 mr-1" />
                {newLabel}
              </button>
            )}
            <div className="flex items-center ml-1 border-l border-slate-200 pl-2 gap-1.5">
              <button
                type="button"
                className="tb-icon !w-8 !h-8"
                onClick={toggleAiPanel}
                title={aiCollapsed ? 'Abrir IA' : 'Fechar IA'}
                aria-label={aiCollapsed ? 'Abrir IA' : 'Fechar IA'}
              >
                {aiCollapsed ? <PanelLeftClose className="w-3.5 h-3.5" /> : <PanelRightClose className="w-3.5 h-3.5" />}
              </button>
              <button
                type="button"
                className="tb-icon !w-8 !h-8"
                onClick={() => openTab("/alerts", "Alertas", Bell)}
                title="Abrir alertas"
                aria-label="Abrir alertas"
              >
                <Bell className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                className="tb-icon !w-8 !h-8"
                onClick={() => authService.logout()}
                title="Sair"
                aria-label="Sair"
              >
                <LogOut className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>

        {/* CONTENT ROW */}
        <div className={cn("body-row flex-1 overflow-hidden relative", aiCollapsed && "is-ai-hidden")}>
          <div className="content h-full p-4 overflow-y-auto">
            {/* HERE WE RENDER THE MDI TABS */}
            <ModuleShellErrorBoundary moduleName={activeModule.label}>
              <WorkspaceTabRenderer />
            </ModuleShellErrorBoundary>
          </div>

          {/* AI PANEL (NIP Assistance) — LIVE */}
          <AIPanelLive moduleContext={activeModule.id} userName={user?.name || 'Usuário'} />
        </div>
      </div>

      <NovoProcessoModal open={novoOpen} onClose={() => setNovoOpen(false)} moduleId={activeModule.id} moduleLabel={activeModule.label} />
    </div>
  );
}
